import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Receipt } from 'lucide-react';
import { adminService } from '../../../services/adminService';

/**
 * Badge shown on an advance-invoiced order. Expands to reveal who issued the
 * invoice, when, and why — fetched lazily so the orders list stays cheap.
 */
export const AdvanceInvoiceBadge = ({ order }) => {
  const [expanded, setExpanded] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !invoice && order.invoice_id) {
      setLoading(true);
      try {
        setInvoice(await adminService.getInvoiceDetail(order.invoice_id));
      } catch {
        setInvoice({ advance_invoice_reason: 'Could not load invoice details' });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50" data-testid="advance-invoice-panel">
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2 p-3 text-left min-h-[44px]"
        data-testid="advance-invoice-badge"
      >
        <Receipt className="h-4 w-4 text-slate-600 shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">
          Invoiced in advance
        </span>
        {order.invoice_number && (
          <span className="text-xs text-slate-500">{order.invoice_number}</span>
        )}
        <span className="ml-auto text-slate-400">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-1 text-sm text-slate-700" data-testid="advance-invoice-details">
          {loading && <p className="text-xs text-slate-500">Loading…</p>}
          {!loading && invoice && (
            <>
              <p>
                <span className="text-slate-500">Reason:</span>{' '}
                {invoice.advance_invoice_reason || '—'}
              </p>
              <p>
                <span className="text-slate-500">Issued by:</span>{' '}
                {invoice.advance_invoiced_by_name || '—'}
              </p>
              <p>
                <span className="text-slate-500">Issued:</span>{' '}
                {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleString() : '—'}
              </p>
              <p>
                <span className="text-slate-500">Order status at issuance:</span>{' '}
                {(invoice.invoiced_at_order_status || '—').replace(/_/g, ' ')}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
